import type { ReactElement } from 'react';
import { BlockToggle } from '@/components/shared/BlockToggle';
import { EnforcementSource } from '@/components/shared/EnforcementSource';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SkillGroupStateCellProps {
  /** Server-computed state for the group; the cell holds no optimistic state. */
  blocked: boolean;
  /** Whose choice `blocked` is. */
  source: 'group' | 'organization' | undefined;
  groupName: string;
  name: string;
  onAllow: () => void;
  /** Only requests the confirmation; does not itself block. */
  onRequestBlock: () => void;
  pending: boolean;
  /** Owner/manager only. */
  canEdit: boolean;
  /** Why the switch is disabled for a read-only viewer. No message, no tooltip. */
  readOnlyMessage?: string;
}

/**
 * A skill row in a security-group scope (KIR-4701): the shared Allowed /
 * Blocked switch plus whether the choice is the group's own or inherited.
 * The organization scope keeps {@link SkillStateCell} and its Mark-for-delete
 * wording; a group choice is Allow/Block for that group's members.
 */
export function SkillGroupStateCell({
  blocked,
  source,
  groupName,
  name,
  onAllow,
  onRequestBlock,
  pending,
  canEdit,
  readOnlyMessage,
}: SkillGroupStateCellProps): ReactElement {
  const toggle = (
    <BlockToggle
      blocked={blocked}
      name={name}
      onAllow={onAllow}
      onRequestBlock={onRequestBlock}
      disabled={!canEdit || pending}
    />
  );
  return (
    // The row is click-to-expand; a click anywhere in the cell (the read-only
    // tooltip wrapper included) must not toggle it, as in SkillStateCell.
    <div
      className="flex items-center gap-3"
      onClick={event => event.stopPropagation()}
    >
      {!canEdit && readOnlyMessage ? (
        <Tooltip>
          {/* A disabled switch fires no pointer events and skips tab order,
              so the trigger is a focusable wrapper (as in SkillStateCell). */}
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {toggle}
            </span>
          </TooltipTrigger>
          <TooltipContent>{readOnlyMessage}</TooltipContent>
        </Tooltip>
      ) : (
        toggle
      )}
      <EnforcementSource source={source} groupName={groupName} />
    </div>
  );
}
